"use client";

import { unwrapResult } from "@reduxjs/toolkit";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import FadeAnimation from "@/components/atoms/animation/fade-animation";
import TransformAnimation from "@/components/atoms/animation/transform-animation";
import ButtonGroup from "@/components/atoms/button-group/button-group";
import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import UnSavedChangesWarningMessage from "@/components/organisms/un-saved-changes-warning-message/un-saved-changes-warning-message";
import withApplication from "@/components/with-application";
import {
  CruiseEdit,
  QuestionFour,
  QuestionThree,
  QuoteEdit,
  Quote,
  RenewalPage,
  QuestionFive,
} from "@/config/app-constant";
import { Routes } from "@/config/page-view-constants";
import { PRODUCT_DOMAIN } from "@/constants";
import useGtmQuestionSet from "@/hooks/gtm/use-gtm-question-set";
import { useStickyFooter } from "@/hooks/use-media-query/use-media-query-enhanced";
import { useQuestionsState } from "@/providers/questions-state-provider";
import { useAppDispatch } from "@/store/hook";
import {
  saveQuestionDataBFF,
  sessionCheck,
  updateQuoteOnQuestion,
} from "@/store/slices/quote/quote.action";
import { setQuestionData } from "@/store/slices/quote/quote.slice";
import { RootState } from "@/store/store";
import { CruiseContainerProps } from "@/types/CruiseType";
import { QuotePayload } from "@/types/quote/quote-data";
import { TripDetails } from "@/types/TripDetails";
import selectLoationHookFlash, {
  modifyPayloadSave,
} from "@/utils/common.utils";
import {
  answersQuestionThree,
  findAnswerQThreeById,
} from "@/utils/cruise.utils";
import {
  getRouterPathBackButton,
  getRouterPathMainButton,
} from "@/utils/page-routing.utils";
import { handleScrollToRef } from "@/utils/scrollUtils";

import ActionButton from "../../action-button/action-button";
import ConfirmationsModal from "../../quote-section/edit-quote-section/confirmation-modal";
import editQuoteStyle from "../../quote-section/edit-quote-section/edit-quote.module.css";
import BackToRenewal from "../../renewals/back-to-renewal/back-to-renewal";

const initialPayload: QuotePayload = {
  type: "IntermediateData",
  channel: "ONLINE",
  attributes: {
    domain: PRODUCT_DOMAIN,
    lastVisitedPage: "test",
  },
};

function CruiseContainer({
  buttonDisable,
  usagePage,
  pageContent,
  formRef,
  ctaRef,
  application,
}: Readonly<CruiseContainerProps>) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const dispatch = useAppDispatch();
  const { selected, setSelectedState } = useQuestionsState();

  const [modalOpen, setModalOpen] = useState(false);
  const [payload, setPayload] = useState<QuotePayload>(initialPayload);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [isSubmitBtnClicked, setIsSubmitBtnClicked] = useState<boolean>(false);
  const [isOpenUnsavedChangesModal, setIsOpenUnsavedChangesModal] =
    useState(false);
  const actionButtonRef = useRef<HTMLDivElement | null>(null);
  const isStickyFooter = useStickyFooter();

  const editPage = searchParams.get(CruiseEdit);
  const {
    questionText = "Question Text",
    ctaButtonText = "Button Text",
    finishEditingBtnText = "Finish Editing",
    cancelEditingBtnText = "Cancel Editing",
    backToRenewalsText,
  } = pageContent;
  const quoteResponse = useSelector((state: RootState) => state.quote);
  const { tripDetails } = quoteResponse?.attributes || undefined;

  useEffect(() => {
    if (selected && tripDetails) {
      const tripDetailsModified: TripDetails = {
        ...tripDetails,
        goingOnCruise: findAnswerQThreeById(selected as number)?.value,
      };
      setPayload(modifyPayloadSave(quoteResponse, tripDetailsModified));
    }
  }, [quoteResponse, selected, tripDetails]);

  const handleAnswerSelection = (answerNo: number) => {
    if (answerNo) {
      if (selected === answerNo) {
        setSelectedState(0);
        return;
      }

      const tripDetailsModified: TripDetails = {
        ...tripDetails,
        goingOnCruise: findAnswerQThreeById(answerNo)?.value,
      };

      setPayload(modifyPayloadSave(quoteResponse, tripDetailsModified));
      setSelectedState(answerNo);
    }
  };

  const handleSubmit = async () => {
    if (!buttonDisable) {
      setIsSubmitBtnClicked(true);
      setButtonLoading(true);
      try {
        dispatch(setQuestionData(payload));

        if (editPage && quoteResponse?.referenceId) {
          const result = await dispatch(
            updateQuoteOnQuestion({
              payload: { ...payload, viewPage: Routes.Cruise },
              router,
              dispatch,
            })
          );
          const originalPromiseResult = unwrapResult(result);
          if (application === "renewal") {
            router.push("/");
          } else if (originalPromiseResult) {
            router.push(`/${Quote}`);
          }
        } else if (!quoteResponse?.referenceId) {
          const questionData = await dispatch(
            saveQuestionDataBFF({
              payload: { ...payload, viewPage: Routes.Cruise },
              dispatch,
              router,
            })
          );
          const originalPromiseResult = unwrapResult(questionData);
          if (originalPromiseResult) {
            router.push(getRouterPathMainButton(QuestionFour));
          }
        } else {
          dispatch(sessionCheck({ dispatch, router }));
          if (application === RenewalPage) {
            router.push(getRouterPathMainButton(QuestionFive));
          } else {
            router.push(getRouterPathMainButton(QuestionFour));
          }
        }

        setButtonLoading(false);
      } catch {
        setButtonLoading(false);
      }
    }
  };

  const handleBackButton = () => {
    if (application === "renewal" && editPage) {
      setIsOpenUnsavedChangesModal(true);
      return;
    }
    router.push(getRouterPathBackButton(QuestionFour));
  };

  const handleCancelEdit = () => {
    setModalOpen(true);
  };

  const toLocationData = selectLoationHookFlash(
    payload?.attributes?.tripDetails ?? undefined
  );

  useGtmQuestionSet({
    pageTitle: QuestionFour,
    tripType: payload?.attributes?.tripDetails?.policyType ?? undefined,
    pageUrl: typeof window !== "undefined" ? window.location.href : "",
    destination: toLocationData.destination,
    destinationAdditions: toLocationData.destinationAdditions,
    tripExtra: payload?.attributes?.tripDetails?.goingOnCruise
      ? QuestionFour
      : undefined,
    travelFrom: tripDetails?.fromLocation ?? undefined,
    isSubmitBtnClicked,
  });

  const onScrollTrigger = () => {
    handleScrollToRef(actionButtonRef, !isStickyFooter);
  };

  const renderQuestionThreeSection = () => {
    return (
      <div className="" data-testid="renderQuestionThreeSection-test-id">
        {" "}
        {editPage && application !== RenewalPage && (
          <div>
            <LabelButtonWrapper
              hierarchy="tertiary"
              label={cancelEditingBtnText}
              id="cancel-edit-cruise"
              className={`${editQuoteStyle.cancelEditButton}`}
              onClick={handleCancelEdit}
            />
            <ConfirmationsModal
              isOpen={modalOpen}
              handleConfirmBtnClick={() => {
                router.push(`/${Quote}/?${QuoteEdit}=true`);
              }}
              handleCancelmBtnClick={() => {
                setModalOpen(false);
              }}
            />
          </div>
        )}
        {application === RenewalPage && editPage && (
          <div>
            {backToRenewalsText && (
              <BackToRenewal
                label={backToRenewalsText}
                className="pb-spacing-m md:pb-[0px] 
        top-[-2px] !px-[0px] mb-spacing-l md:!mb-spacing-m lg:mx-[0px]"
                onClick={() => setIsOpenUnsavedChangesModal(true)}
              />
            )}

            <UnSavedChangesWarningMessage
              isOpen={isOpenUnsavedChangesModal}
              onContinueClick={() => {
                router.push(`/`);
              }}
              setIsOpen={setIsOpenUnsavedChangesModal}
            />
          </div>
        )}
        <h1
          className=" font-header-font lg:text-[39px] md:text-3xl text-[26px] lg:leading-[44.85px] 
                md:leading-[34.1px] leading-[29.9px] text-text-title md:text-left text-center"
        >
          {questionText}
        </h1>
        <ButtonGroup
          setSelectedAnswer={handleAnswerSelection}
          selectedAnswer={selected as number}
          answerArray={answersQuestionThree}
          location={QuestionThree}
          type="cruise"
          onOptionClick={(id, dataTestId) => {
            const buttonElement = document.querySelector(`#${dataTestId}`);
            if (
              buttonElement?.classList.contains(
                "componnt_lib_toggle_btn_inactive"
              )
            ) {
              onScrollTrigger();
            }
          }}
        />
      </div>
    );
  };

  const editFinishStyle = editPage ? editQuoteStyle.editFinishButton : "";

  return (
    <div
      data-testid="question-three-test-id"
      className={`rounded-radius-xxl 
      bg-surface-neutral-option-1 rounded-b-radius-xxl  
           md:py-spacing-xxl py-spacing-m px-spacing-m h-full`}
    >
      <div className="lg:w-[414px] md:w-[400px] w-full mx-auto relative h-full flex flex-col justify-between">
        <TransformAnimation animationRef={formRef} currentPage={usagePage}>
          {renderQuestionThreeSection()}
        </TransformAnimation>

        <FadeAnimation animationRef={ctaRef}>
          <div ref={actionButtonRef} className={`${editFinishStyle}`}>
            <ActionButton
              page={usagePage}
              ctaButtonText={editPage ? finishEditingBtnText : ctaButtonText}
              disableMain={buttonDisable}
              isLoading={buttonLoading}
              showBackBtn={!editPage}
              submitButtonClick={handleSubmit}
              backButtonClick={handleBackButton}
            />
          </div>
        </FadeAnimation>
      </div>
    </div>
  );
}
export default withApplication(CruiseContainer);
